/**
 * Application store.
 *
 * A small framework-agnostic observable: state is split into slices, every
 * change flows through a named mutation, and subscribers are notified per slice
 * so a listener on "balances" is not woken by a "wallets" change. Each mutation
 * replaces the slice object immutably, so React's useSyncExternalStore (see
 * ./hooks) re-renders exactly the components that read a changed slice.
 *
 * This module imports nothing from React so the api, session, and auth layers
 * can drive state without a framework dependency.
 */

import type {
  AppState,
  SliceName,
  AccountState,
  WalletSummary,
  WalletBalances,
  ActivityItem,
  Approval,
  ApprovalTransaction,
  DeviceCredentialSummary,
  PendingSwap,
  Theme,
  Toast,
} from '../lib/types';
import type { ChainFamily } from '../lib/network';
import {
  type ImportedToken,
  dedupeImported,
  tokenImportKey,
  hasImported,
  MAX_IMPORTED_TOKENS_PER_WALLET,
} from '../lib/importedTokens';

/**
 * localStorage key for the persisted Developer Mode tier selection. The key is
 * versioned so a selection written by an earlier build is not carried forward.
 * An earlier dashboard tier tab, since removed, could persist "mainnet" here; a
 * later build that honors the stored value would then read that orphaned choice
 * and read that orphaned choice for a tier the deployment cannot serve. Bumping
 * the suffix drops any value written under the old key, so the tier resolves to
 * the default (mainnet, clamped to a served tier) until the user makes a fresh
 * choice on the developer-mode page.
 */
const ENV_STORAGE_KEY = 'pmw.env.v2';

/**
 * localStorage key for the active wallet selection.
 *
 * The selection used to be session-only, so every reload dropped it and each
 * screen fell back to "the first wallet that fits" -- landing the user on a
 * different wallet than the one they left. The tier selection immediately above
 * was already persisted; this simply was not, which made it an omission rather
 * than a decision.
 *
 * Versioned like the others so a stored id written under a different scheme is
 * dropped rather than misread. Losing a remembered selection is a non-event --
 * the app falls back exactly as it did before -- which is what makes a bump
 * cheap.
 */
const SELECTED_WALLET_STORAGE_KEY = 'pmw.selectedWallet.v1';

/**
 * Read the persisted wallet selection, or null when there is none.
 *
 * THE RESULT IS A HINT, NOT AN AUTHORITY. It is a bare id read from storage,
 * and nothing here can know whether that wallet still exists, still belongs to
 * this user, or belongs to the tier the session is reading. It is only ever
 * used after being matched against the wallet list the server actually
 * returned; see reconcileSelectedWallet.
 */
function persistedSelectedWallet(): string | null {
  try {
    const raw = localStorage.getItem(SELECTED_WALLET_STORAGE_KEY);
    return raw && raw.trim() !== '' ? raw : null;
  } catch {
    return null;
  }
}

/**
 * Read the persisted Developer Mode selection, defaulting to mainnet for a user
 * who has not made an explicit choice. An exact "testnet" or "mainnet" written by
 * the user is honored, so anyone who deliberately switched to testnet keeps it; a
 * missing key, a corrupt value, or an environment without storage all resolve to
 * mainnet, the default tier for a new user. The selection is still clamped to a
 * served tier by effectiveEnvironment, so a deployment that does not serve
 * mainnet resolves the label to testnet regardless of this default.
 */
function persistedSelectedEnv(): 'testnet' | 'mainnet' {
  try {
    const saved = localStorage.getItem(ENV_STORAGE_KEY);
    if (saved === 'testnet') return 'testnet';
    if (saved === 'mainnet') return 'mainnet';
    // No explicit choice yet: a new user defaults to mainnet.
    return 'mainnet';
  } catch {
    return 'mainnet';
  }
}

/**
 * localStorage key for the per-wallet hidden-token sets the Manage-crypto screen
 * writes. Versioned like the tier key so the identity-key scheme can change in a
 * later build without a stored value from an earlier scheme being misread: a bump
 * drops the old map and the wallet's Tokens tab returns to showing everything
 * until the user hides tokens again under the new scheme.
 */
const HIDDEN_TOKENS_STORAGE_KEY = 'pmw.hiddenTokens.v1';

/**
 * Read the persisted per-wallet hidden-token map, defaulting to an empty map so
 * nothing is hidden. The stored value is validated to the expected shape (an
 * object of wallet id to an array of string keys) before it is trusted, so a
 * corrupt or foreign value fails safe to nothing hidden rather than throwing at
 * boot or hiding tokens the user never chose to hide. Any parse or storage error
 * falls back the same way.
 */
function persistedHiddenTokens(): Record<string, string[]> {
  try {
    const raw = localStorage.getItem(HIDDEN_TOKENS_STORAGE_KEY);
    if (!raw) return {};
    const parsed: unknown = JSON.parse(raw);
    if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) return {};
    const result: Record<string, string[]> = {};
    for (const [walletId, keys] of Object.entries(parsed as Record<string, unknown>)) {
      if (!Array.isArray(keys)) continue;
      const stringKeys = keys.filter((key): key is string => typeof key === 'string');
      // Drop an entry that ends up empty after filtering so the map stays canonical
      // (a wallet with nothing hidden simply has no key), matching what the setter
      // writes.
      if (stringKeys.length > 0) result[walletId] = stringKeys;
    }
    return result;
  } catch {
    return {};
  }
}

/**
 * localStorage key for the per-wallet imported custom-token lists the
 * Manage-crypto import writes. Versioned like the other persistence keys so the
 * stored shape can change in a later build without an older value being misread:
 * a bump drops the old map and the wallet's imports return to empty until the user
 * imports again. This is the local source of truth for imports today; a later
 * change syncs the same records to the server through the same add and remove
 * interface, so the storage key is the only piece that changes then.
 */
const IMPORTED_TOKENS_STORAGE_KEY = 'pmw.importedTokens.v1';

/**
 * Validate one unknown entry to the ImportedToken shape, returning the normalized
 * record or null when it does not carry the required fields. The stored value is
 * untrusted, so a foreign or corrupt entry is dropped rather than trusted; the
 * network is optional and kept only when it is a string. Decimals must be a finite
 * number so a bad value cannot later mis-scale a displayed balance.
 */
function parseImportedToken(value: unknown): ImportedToken | null {
  if (typeof value !== 'object' || value === null) return null;
  const v = value as Record<string, unknown>;
  const chain = v.chain;
  const contract = v.contract;
  const symbol = v.symbol;
  const name = v.name;
  const decimals = v.decimals;
  if (typeof chain !== 'string' || chain === '') return null;
  if (typeof contract !== 'string' || contract === '') return null;
  if (typeof symbol !== 'string') return null;
  if (typeof name !== 'string') return null;
  if (typeof decimals !== 'number' || !Number.isFinite(decimals)) return null;
  const token: ImportedToken = { chain, contract, symbol, name, decimals };
  if (typeof v.network === 'string' && v.network !== '') token.network = v.network;
  return token;
}

/**
 * Read the persisted per-wallet imported-token map, defaulting to an empty map so
 * nothing is imported. The stored value is validated to the expected shape (an
 * object of wallet id to an array of imported-token records) before it is trusted,
 * with each entry deduped by its identity and capped at the per-wallet limit, so a
 * corrupt or oversized value fails safe rather than throwing at boot or importing
 * tokens the user never chose. Any parse or storage error falls back the same way.
 */
function persistedImportedTokens(): Record<string, ImportedToken[]> {
  try {
    const raw = localStorage.getItem(IMPORTED_TOKENS_STORAGE_KEY);
    if (!raw) return {};
    const parsed: unknown = JSON.parse(raw);
    if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) return {};
    const result: Record<string, ImportedToken[]> = {};
    for (const [walletId, entries] of Object.entries(parsed as Record<string, unknown>)) {
      if (!Array.isArray(entries)) continue;
      const tokens: ImportedToken[] = [];
      for (const entry of entries) {
        const token = parseImportedToken(entry);
        if (token) tokens.push(token);
      }
      // Dedupe by identity and bound to the cap so a stored value that predates or
      // sidesteps the cap cannot restore an oversized list. Drop a wallet that ends
      // up empty so the map stays canonical, matching what the setter writes.
      const bounded = dedupeImported(tokens).slice(0, MAX_IMPORTED_TOKENS_PER_WALLET);
      if (bounded.length > 0) result[walletId] = bounded;
    }
    return result;
  } catch {
    return {};
  }
}

/**
 * localStorage key for the in-flight swap tracker. Versioned like the other
 * persistence keys so the record shape can change in a later build without an
 * older value being misread: a bump drops the stored map, and any swap it held
 * simply falls back to the server tracker's Activity view rather than resuming a
 * client poll. This is the durable handle that lets a submitted swap keep being
 * polled across a reload or a navigation away from the Swap screen, which is the
 * gap that let a fill land unnoticed while the user was elsewhere.
 */
const PENDING_SWAPS_STORAGE_KEY = 'pmw.pendingSwaps.v1';

/**
 * Validate one unknown entry to the PendingSwap shape, returning the normalized
 * record or null when it lacks the required fields. The stored value is
 * untrusted, so a foreign or corrupt entry is dropped rather than trusted. The
 * identity and both chain ids are required (the poller and the explorer links
 * cannot work without them), as is a poll key for every provider that cannot
 * recover one later; the display and progress fields are
 * defaulted so a partially written record still resumes rather than being
 * discarded, since dropping it would strand an in-flight swap.
 */
function parsePendingSwap(value: unknown): PendingSwap | null {
  if (typeof value !== 'object' || value === null) return null;
  const v = value as Record<string, unknown>;
  const id = v.id;
  const provider = v.provider;
  const pollKey = v.pollKey;
  const fromChainId = v.fromChainId;
  const toChainId = v.toChainId;
  const status = v.status;
  if (typeof id !== 'string' || id === '') return null;
  if (typeof provider !== 'string' || provider === '') return null;
  if (typeof pollKey !== 'string') return null;
  // An Across swap is deliberately persisted BEFORE its poll key is known: Across is
  // polled by the origin deposit hash, which the tracker back-fills from the first
  // origin poll (see resolvePollKey and the execute path, which both special-case it).
  // Rejecting an empty key here dropped exactly those records on reload — a real
  // in-flight swap left with no polling resume, no stepper and no banner. Every other
  // provider polls by a requestId it must already hold, so an empty key there is a
  // corrupt record with nothing to poll and no back-fill that could rescue it.
  if (pollKey === '' && provider.toLowerCase() !== 'across') return null;
  if (typeof fromChainId !== 'number' || !Number.isFinite(fromChainId)) return null;
  if (typeof toChainId !== 'number' || !Number.isFinite(toChainId)) return null;
  if (typeof status !== 'string' || status === '') return null;
  const str = (x: unknown, fallback = ''): string => (typeof x === 'string' ? x : fallback);
  const numOrNull = (x: unknown): number | null =>
    typeof x === 'number' && Number.isFinite(x) ? x : null;
  const now = Date.now();
  const swap: PendingSwap = {
    id,
    provider,
    pollKey,
    fromChainId,
    toChainId,
    fromChainKey: str(v.fromChainKey),
    toChainKey: str(v.toChainKey),
    sendAmount: str(v.sendAmount),
    sendSymbol: str(v.sendSymbol),
    expectedOut: str(v.expectedOut),
    outSymbol: str(v.outSymbol),
    originTxHash: typeof v.originTxHash === 'string' ? v.originTxHash : null,
    destTxHash: typeof v.destTxHash === 'string' ? v.destTxHash : null,
    status,
    etaSeconds: numOrNull(v.etaSeconds),
    startedAt: numOrNull(v.startedAt) ?? now,
    updatedAt: numOrNull(v.updatedAt) ?? now,
  };
  if (typeof v.subStatus === 'string' && v.subStatus !== '') swap.subStatus = v.subStatus;
  // Optional and NOT validated into a rejection: a record missing them is from an
  // earlier build and is still a real in-flight swap. Dropping it would lose the
  // polling resume, the stepper and the banner for a swap that is actually running.
  if (typeof v.trackingId === 'string' && v.trackingId !== '') swap.trackingId = v.trackingId;
  if (typeof v.originStatus === 'string' && v.originStatus !== '') {
    swap.originStatus = v.originStatus;
  }
  // The ambiguity latch MUST survive a reload — it exists precisely for the
  // client that lost its state mid-submit.
  if (v.ambiguous === true) swap.ambiguous = true;
  if (v.originReported === true) swap.originReported = true;
  return swap;
}

/**
 * Record the server's ORIGIN-axis answer for one tracked swap.
 *
 * SEPARATE FROM setPendingSwapStatus on purpose. That one moves the PROVIDER
 * axis, and the two are independent: a provider that has said nothing yet does
 * not mean the origin failed, and a verified origin does not mean the swap is
 * complete. One setter touching both would be the conflation this whole design
 * exists to prevent.
 *
 * A no-op for an unknown id, and for a value that has not changed, so a resume
 * that finds nothing new writes nothing and re-renders nothing.
 *
 * @param id The tracked swap's gateway transaction id.
 * @param originStatus The server's origin_status.
 */
export function setPendingSwapOriginStatus(id: string, originStatus: string): void {
  const current = state.pendingSwaps.byId[id];
  if (!current || current.originStatus === originStatus) return;
  upsertPendingSwap({ ...current, originStatus, updatedAt: Date.now() });
}

/**
 * Read the persisted in-flight swap map, defaulting to an empty map. The stored
 * value is validated to the expected shape (an object of transaction id to a
 * pending-swap record) before it is trusted, dropping any entry that fails
 * validation, so a corrupt or foreign value fails safe to nothing tracked rather
 * than throwing at boot. Any parse or storage error falls back the same way.
 */
function persistedPendingSwaps(): Record<string, PendingSwap> {
  try {
    const raw = localStorage.getItem(PENDING_SWAPS_STORAGE_KEY);
    if (!raw) return {};
    const parsed: unknown = JSON.parse(raw);
    if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) return {};
    const result: Record<string, PendingSwap> = {};
    for (const [id, entry] of Object.entries(parsed as Record<string, unknown>)) {
      const swap = parsePendingSwap(entry);
      // Keep the map canonical by keying on the record's own id, so a mismatched
      // outer key from a hand-edited store cannot desync the poller's lookup.
      if (swap) result[swap.id] = { ...swap, id };
    }
    return result;
  } catch {
    return {};
  }
}

/** Build a fresh initial state. Used at boot and by {@link resetStore}. */
function initialState(): AppState {
  return {
    accounts: {
      current: {
        orgId: null,
        userId: null,
        sessionEpoch: 0,
        token: null,
        authenticated: false,
        passkeySignup: false,
        freshLogin: false,
      },
    },
    // Seeded from storage, deliberately BEFORE any wallet list exists. The id is
    // unvalidated at this point and is reconciled against the real list the
    // moment one arrives (see setWallets); seeding here is what lets the first
    // paint after a reload show the right wallet instead of flashing a default.
    wallets: {
      list: [],
      selectedId: persistedSelectedWallet(),
      loading: false,
      error: null,
      settled: false,
    },
    balances: { byWalletId: {}, loading: false, error: null, settled: false },
    activity: { items: [], nextCursor: null, loading: false, error: null, settled: false },
    approvals: {
      items: [],
      txById: {},
      loading: false,
      settled: false,
      error: null,
      actingId: null,
      actionError: null,
    },
    network: {
      online: true,
      environment: '',
      servesMainnet: null,
      servesTestnet: null,
      // Seed from persisted storage so a reload keeps the user's choice.
      selectedEnv: persistedSelectedEnv(),
      bitcoinNetwork: null,
      bitcoinMainnetServed: null,
      inFlight: 0,
    },
    devices: {
      items: [],
      loaded: false,
      busy: false,
      error: null,
      // Resolved once at boot from the WebAuthn feature check; a browser without
      // WebAuthn keeps this false so the Security enroll control stays hidden.
      supported: false,
    },
    // Seed the in-flight swap map from storage so a reload resumes polling every
    // swap that had not yet settled. Defaults to empty, meaning nothing tracked.
    pendingSwaps: { byId: persistedPendingSwaps(), inlineId: null },
    ui: {
      theme: 'auto',
      updateAvailable: false,
      toasts: [],
      // Seeded from the SAME persisted id as wallets.selectedId above, not from
      // a flat 'all'.
      //
      // The two are one selection wearing two names -- setActiveWallet writes
      // both -- but only wallets.selectedId was ever restored. So a reload put
      // this back to 'all', which the home reads as "no choice made yet" and
      // answers by auto-picking a wallet, silently discarding the one the user
      // had chosen. Seeding here is what makes the persisted choice actually
      // survive; 'all' now means the user really is on the aggregate.
      //
      // A stored id that is gone, or is off the active tier, still resolves back
      // to 'all' downstream (setWallets drops it; Dashboard's selectedOnTier
      // falls back), so a stale id cannot pin the home to a wallet that is not
      // there.
      selectedAccountId: persistedSelectedWallet() ?? 'all',
      networkFilter: null,
      showDust: false,
      // Seed the hidden-token map from storage so a reload keeps each wallet's
      // Manage-crypto choices. Defaults to an empty map, meaning nothing hidden.
      hiddenTokens: persistedHiddenTokens(),
      // Seed the imported-token map from storage so a reload keeps each wallet's
      // custom-token imports. Defaults to an empty map, meaning nothing imported.
      importedTokens: persistedImportedTokens(),
    },
  };
}

/** The live state. Read through {@link getSlice}; mutate only via the actions. */
let state: AppState = initialState();

type Listener = () => void;

/** Per-slice listener sets, plus '*' for subscribers to every change. */
const listeners = new Map<SliceName | '*', Set<Listener>>();

/** Notify the listeners of one slice, then the wildcard listeners. */
function notify(slice: SliceName): void {
  listeners.get(slice)?.forEach((fn) => fn());
  listeners.get('*')?.forEach((fn) => fn());
}

/**
 * Replace one slice by merging a patch, then publish. Producing a new slice
 * object is what lets React detect the change by reference.
 */
function patchSlice<K extends SliceName>(name: K, patch: Partial<AppState[K]>): void {
  state = { ...state, [name]: { ...state[name], ...patch } };
  notify(name);
}

// CROSS-TAB LATCH SYNC (B1). pendingSwaps carries the swap form's double-spend
// gate (the ambiguous latch), and every write persists it — but localStorage
// writes are invisible to OTHER open tabs without this listener, so a latch
// raised in one tab left a second tab's form open, and a latch acknowledged
// away in one tab kept haunting the others. `storage` fires only in the tabs
// that did NOT write, so this never loops on its own writes; the freshly
// persisted map simply replaces this tab's view (inlineId stays — it is
// per-tab UI state, not shared). Guarded because the store is also imported in
// node test environments where window does not exist.
if (typeof window !== 'undefined') {
  window.addEventListener('storage', (event) => {
    if (event.key !== PENDING_SWAPS_STORAGE_KEY) return;
    patchSlice('pendingSwaps', { byId: persistedPendingSwaps() });
  });
}

/** Read a slice's current value. Stable by reference until the slice changes. */
export function getSlice<K extends SliceName>(name: K): AppState[K] {
  return state[name];
}

/**
 * Subscribe to changes on a single slice.
 *
 * @param slice Slice to watch, or '*' for every change.
 * @param handler Called after each matching change.
 * @returns Unsubscribe function the caller must invoke.
 */
export function subscribe(slice: SliceName | '*', handler: Listener): () => void {
  let set = listeners.get(slice);
  if (!set) {
    set = new Set();
    listeners.set(slice, set);
  }
  set.add(handler);
  return () => set.delete(handler);
}

/* Named mutations. Every state change flows through one of these. */

/** Merge fields into the active account context. */
export function setAccount(partial: Partial<AccountState>): void {
  patchSlice('accounts', { current: { ...state.accounts.current, ...partial } });
}

/**
 * Clear the account context, for example on logout.
 *
 * Also drops the enrolled-device list, for the same reason logout drops tracked
 * swaps: it is the previous account's data and must not be visible to, or answer
 * questions for, whoever signs in next.
 *
 * sessionEpoch is deliberately CARRIED ACROSS, not reset. Resetting it would let
 * the next session land on a value already seen, and every cache keyed on it would
 * skip the reload it exists to force.
 */
export function clearAccount(): void {
  patchSlice('accounts', {
    current: {
      orgId: null,
      userId: null,
      sessionEpoch: state.accounts.current.sessionEpoch,
      token: null,
      authenticated: false,
      passkeySignup: false,
      freshLogin: false,
    },
  });
  resetDevices();
  resetAccountData();
}

/**
 * Open a new session: bump the epoch and drop the previous account's device list.
 *
 * Called by storeLogin, which every route to a session passes through. The reset
 * closes the window between a login and the refetch landing, during which the gate
 * on the create-passkey prompt — and hasEnrolledDevice(), which decides whether the
 * send path runs a device assertion — would otherwise answer for the old account.
 * The bump is what makes the refetch happen at all when `authenticated` never flips.
 */
export function beginSession(): void {
  patchSlice('accounts', {
    current: {
      ...state.accounts.current,
      sessionEpoch: state.accounts.current.sessionEpoch + 1,
    },
  });
  resetDevices();
  resetAccountData();
}

/**
 * Drop every account-scoped read back to "never loaded".
 *
 * Not merely cosmetic. `settled` is what every screen's skeleton gate reads, so
 * leaving it true across a session change would show the PREVIOUS account's
 * wallets, balances, and history as settled fact to whoever signs in next, until
 * the refetch happened to land. Clearing the data and the flag together means the
 * next session starts on skeletons and paints only its own data.
 */
export function resetAccountData(): void {
  // Clear the PERSISTED selection too, not only the in-memory one. This runs on
  // sign-out, and a stored id surviving it would be seeded back at the next
  // boot and belong to the previous account -- harmless in effect, since the
  // reconciliation in setWallets drops an id the new list does not contain, but
  // there is no reason to leave one user's wallet id on the device for the next
  // one to load.
  selectWallet(null);
  patchSlice('wallets', { list: [], selectedId: null, loading: false, error: null, settled: false });
  patchSlice('balances', { byWalletId: {}, loading: false, error: null, settled: false });
  patchSlice('activity', {
    items: [],
    nextCursor: null,
    loading: false,
    error: null,
    settled: false,
  });
  patchSlice('approvals', {
    items: [],
    txById: {},
    loading: false,
    settled: false,
    error: null,
    actingId: null,
    actionError: null,
  });
}

/**
 * Drop the enrolled-device list back to "not loaded".
 *
 * `supported` is preserved: it is a property of the browser, resolved once at boot
 * from the WebAuthn feature check, not of the account.
 */
export function resetDevices(): void {
  patchSlice('devices', { items: [], loaded: false, busy: false, error: null });
}

/**
 * Mark or clear the flag that says the current session was created by a passkey
 * signup. Start.tsx sets it true immediately after recording the session.
 *
 * Nothing renders because of it, and it is not what keeps the create-a-passkey
 * prompt away from a fresh signup — freshLogin already does that, because
 * storeLogin raises freshLogin only for a non-passkey provider.
 * CreatePasskeyPromptGate reads this too, as a redundant second guard.
 * storeLogin clears it on every login so it cannot leak into a later session.
 *
 * @param value True right after a passkey signup, false on any other login.
 */
export function setPasskeySignup(value: boolean): void {
  setAccount({ passkeySignup: value });
}

/**
 * Mark or clear the flag that says the current session was created by a fresh
 * OAuth or OTP login. storeLogin sets it true for a non-passkey provider just as
 * the session is recorded, so the app can offer the one-time "create a passkey"
 * prompt to a user who signed in with a provider and has no passkey enrolled. The
 * prompt clears it once it is shown or dismissed. It lives in memory only, so a
 * later reload or a passkey login never re-triggers the prompt.
 *
 * @param value True right after a fresh OAuth or OTP login, false once handled.
 */
export function setFreshLogin(value: boolean): void {
  setAccount({ freshLogin: value });
}

/** Mark the wallet list as loading or settled. */
export function setWalletsLoading(loading: boolean): void {
  patchSlice('wallets', { loading });
}

/**
 * Replace the wallet list and clear any prior error.
 *
 * This is also where a restored selection is RECONCILED, because it is the
 * first moment the truth is available. A persisted id is just a string until a
 * real list arrives: the wallet may have been deleted, may belong to another
 * user after a re-login, or may sit on the tier the session is not reading. An
 * id with no match is dropped here, which returns the app to the same fallback
 * it used before anything was persisted.
 *
 * Dropping it also CLEARS THE STORED VALUE rather than only the in-memory one.
 * Leaving the id in storage would have it restored again on the next reload and
 * dropped again on the next list, silently forever.
 */
export function setWallets(list: WalletSummary[]): void {
  const selectedId = getSlice('wallets').selectedId;
  if (selectedId !== null && !list.some((w) => w.id === selectedId)) {
    selectWallet(null);
  }
  patchSlice('wallets', { list, error: null, loading: false, settled: true });
}

/** Record a wallet load failure. */
export function setWalletsError(message: string): void {
  patchSlice('wallets', { error: message, loading: false, settled: true });
}

/**
 * Select the active wallet by id, or null to clear.
 *
 * Persisted, so the choice survives a reload. Writing here rather than at each
 * call site means every path that changes the selection is covered -- the
 * account sheet, the Receive chain picker, a fresh create, and the tier flip
 * that clears it -- without any of them having to remember.
 */
export function selectWallet(walletId: string | null): void {
  try {
    if (walletId === null) localStorage.removeItem(SELECTED_WALLET_STORAGE_KEY);
    else localStorage.setItem(SELECTED_WALLET_STORAGE_KEY, walletId);
  } catch {
    // Storage can be unavailable (private mode, disabled). The in-memory patch
    // still applies for this session; only cross-reload persistence is lost.
  }
  patchSlice('wallets', { selectedId: walletId });
}

/**
 * Insert or replace one wallet at the front of the list. A wallet already
 * present under the same id is replaced in place, so a re-create does not
 * duplicate it. Used after a create so the new wallet appears immediately.
 */
export function upsertWallet(wallet: WalletSummary): void {
  const rest = state.wallets.list.filter((w) => w.id !== wallet.id);
  patchSlice('wallets', { list: [wallet, ...rest], error: null });
}

/** Mark balances as loading or settled. */
export function setBalancesLoading(loading: boolean): void {
  patchSlice('balances', { loading });
}

/** Store the assembled balance view for one wallet. */
export function setWalletBalances(walletId: string, balances: WalletBalances): void {
  patchSlice('balances', {
    byWalletId: { ...state.balances.byWalletId, [walletId]: balances },
    error: null,
    loading: false,
    settled: true,
  });
}

/** Record a balance load failure. */
export function setBalancesError(message: string): void {
  patchSlice('balances', { error: message, loading: false, settled: true });
}

/** Mark activity history as loading or settled. */
export function setActivityLoading(loading: boolean): void {
  patchSlice('activity', { loading });
}

/** Replace the activity history page. */
export function setActivity(items: ActivityItem[], nextCursor: string | null): void {
  patchSlice('activity', { items, nextCursor, error: null, loading: false, settled: true });
}

/** Append another page of activity history. */
export function appendActivity(items: ActivityItem[], nextCursor: string | null): void {
  patchSlice('activity', {
    items: [...state.activity.items, ...items],
    nextCursor,
    loading: false,
  });
}

/**
 * Prepend one activity item, replacing any existing item with the same id so a
 * status poll updates the row in place rather than duplicating it.
 */
export function prependActivity(item: ActivityItem): void {
  const rest = state.activity.items.filter((i) => i.id !== item.id);
  patchSlice('activity', { items: [item, ...rest] });
}

/** Record an activity load failure. */
export function setActivityError(message: string): void {
  patchSlice('activity', { error: message, loading: false, settled: true });
}

/** Mark the approvals list as loading or settled. */
export function setApprovalsLoading(loading: boolean): void {
  patchSlice('approvals', { loading });
}

/**
 * Replace the approvals list and clear any prior load error. Clearing the error
 * on a successful reload keeps a stale banner from lingering after a retry.
 */
export function setApprovals(items: Approval[]): void {
  patchSlice('approvals', { items, error: null, loading: false, settled: true });
}

/** Record an approvals load failure. */
export function setApprovalsError(message: string): void {
  patchSlice('approvals', { error: message, loading: false, settled: true });
}

/**
 * Store one joined transaction detail for an approval, keyed by transaction id.
 * A null value records a join that failed so the view stops retrying and shows a
 * degraded detail. The join is only for display; it never changes what is signed.
 */
export function setApprovalTransaction(txId: string, tx: ApprovalTransaction | null): void {
  patchSlice('approvals', { txById: { ...state.approvals.txById, [txId]: tx } });
}

/**
 * Set the approval a decision is in flight for, or null when settled, and clear
 * any prior action error when a new action starts. One id at a time so a second
 * action cannot race an approve or reject already dispatched.
 */
export function setApprovalActing(id: string | null): void {
  patchSlice('approvals', { actingId: id, actionError: id ? null : state.approvals.actionError });
}

/** Record an approve or reject failure to surface in plain language. */
export function setApprovalActionError(message: string | null): void {
  patchSlice('approvals', { actionError: message });
}

/**
 * Replace one approval in place by id, for the optimistic row update after a
 * decision returns. An id not present is a no-op, so a row removed by a reload
 * mid-action is never resurrected.
 */
export function upsertApproval(approval: Approval): void {
  const present = state.approvals.items.some((a) => a.id === approval.id);
  if (!present) return;
  patchSlice('approvals', {
    items: state.approvals.items.map((a) => (a.id === approval.id ? approval : a)),
  });
}

/** Update the browser online flag. */
export function setOnline(online: boolean): void {
  patchSlice('network', { online });
}

/** Set the deployment network environment label. */
export function setEnvironment(environment: string): void {
  patchSlice('network', { environment });
}

/**
 * Patch the full deployment signals in one mutation: the cosmetic environment
 * label plus the authoritative tier flags and Bitcoin network. Views classify
 * tier from these (see lib/network), never from the environment label alone.
 *
 * The user's Developer Mode selection (selectedEnv) is intentionally left
 * untouched here so loading config after a reload never overwrites a choice the
 * user made. effectiveEnvironment clamps that selection to a served tier.
 */
export function setDeployment(d: {
  environment: string;
  servesMainnet: boolean | null;
  servesTestnet: boolean | null;
  bitcoinNetwork: string | null;
  bitcoinMainnetServed: boolean | null;
}): void {
  patchSlice('network', {
    environment: d.environment,
    servesMainnet: d.servesMainnet,
    servesTestnet: d.servesTestnet,
    bitcoinNetwork: d.bitcoinNetwork,
    bitcoinMainnetServed: d.bitcoinMainnetServed,
  });
}

/**
 * Set the Developer Mode tier selection and persist it. Developer Mode ON means
 * testnet, OFF means mainnet. The choice is written to localStorage so a reload
 * keeps it, and the persisted value is read back into initialState at boot.
 * effectiveEnvironment clamps the selection to a tier the deployment serves.
 *
 * Flipping the tier also clears the shared active target. The active wallet
 * (wallets.selectedId) and the home account context (ui.selectedAccountId) point
 * at a concrete wallet on the tier that was active when they were set, so
 * carrying them across a tier flip would leave a mainnet wallet selected while
 * the session reads testnet, which is the wrong-tier send path this clears.
 * Send and Receive fall back to the first wallet on the new tier once the
 * selection is null, so clearing it here is the fail-safe: a stale cross-tier
 * selection can never survive a flip to become a Send source or Receive target.
 */
export function setSelectedEnv(env: 'testnet' | 'mainnet'): void {
  try {
    localStorage.setItem(ENV_STORAGE_KEY, env);
  } catch {
    // Storage can be unavailable (private mode, disabled). The in-memory patch
    // still takes effect for this session; only cross-reload persistence is lost.
  }
  patchSlice('network', { selectedEnv: env });
  // Drop the active-wallet selection and the home account context so a wallet on
  // the previous tier cannot remain the target after the flip.
  selectWallet(null);
  setSelectedAccount('all');
}

/**
 * Adjust the in-flight request counter. The api layer calls this around each
 * request so the UI can show a global activity indicator.
 */
export function adjustInFlight(delta: number): void {
  patchSlice('network', { inFlight: Math.max(0, state.network.inFlight + delta) });
}

/* Device-passkey mutations. The gateway is the credential authority; these only
   mirror the enrolled list so the send path can decide whether to run the device
   assertion and the Security section can render. */

/**
 * Record whether this browser supports WebAuthn. Set once at boot from the
 * feature check so the Security section can hide the enroll control on an
 * unsupported browser without blocking any existing flow.
 */
export function setDevicesSupported(supported: boolean): void {
  patchSlice('devices', { supported });
}

/** Mark a device load, enroll, or remove as in flight or settled. */
export function setDevicesBusy(busy: boolean): void {
  patchSlice('devices', { busy });
}

/**
 * Replace the enrolled device list and mark it loaded, clearing any prior error.
 * hasEnrolledDevice is derived from this list at read time, so a load, an enroll,
 * and a remove all keep the send path's device decision in sync through this one
 * mutation.
 */
export function setDevices(items: DeviceCredentialSummary[]): void {
  patchSlice('devices', { items, loaded: true, busy: false, error: null });
}

/** Record a device action failure to surface in plain language. */
export function setDevicesError(message: string | null): void {
  patchSlice('devices', { error: message, busy: false });
}

/**
 * Whether the caller has at least one enrolled device credential. The send path
 * reads this to decide whether to run the device assertion before approving, so
 * it is derived from the live list rather than a separate flag that could drift.
 */
export function hasEnrolledDevice(): boolean {
  return state.devices.items.length > 0;
}

/** Set the theme preference. The document attribute flip is applied by the UI. */
export function setTheme(theme: Theme): void {
  patchSlice('ui', { theme });
}

/** Flag that a new app version is waiting to activate. */
export function setUpdateAvailable(available: boolean): void {
  patchSlice('ui', { updateAvailable: available });
}

/**
 * Set the home account context. "all" aggregates every wallet; otherwise a
 * wallet id shows that wallet's holdings alone. This is a view filter and is
 * kept distinct from wallets.selectedId (the Send and Receive source wallet), so
 * choosing a concrete account here also calls selectWallet at the call site.
 */
export function setSelectedAccount(id: string): void {
  patchSlice('ui', { selectedAccountId: id });
}

/**
 * Set the single app-wide active target for Send and Receive.
 *
 * The app has one shared "which wallet am I acting on" selection so choosing a
 * wallet in Receive is the same selection Send targets, and the reverse. That
 * selection is wallets.selectedId, the concrete wallet id. This setter promotes
 * one wallet as that target and, in the same mutation batch, mirrors it into the
 * home account context (ui.selectedAccountId) so the home header, the switcher,
 * Send, and Receive all agree on one active wallet without a second source of
 * truth to drift out of sync.
 *
 * The active chain is never stored separately: it is derived from the selected
 * wallet's resolved chain (resolveWalletChain over the balances view), so it
 * cannot disagree with the wallet the selection points at.
 *
 * @param walletId The wallet to make the active Send and Receive target.
 */
export function setActiveWallet(walletId: string): void {
  selectWallet(walletId);
  setSelectedAccount(walletId);
}

/** Set the home network-family filter, or null for All. */
export function setNetworkFilter(family: ChainFamily | null): void {
  patchSlice('ui', { networkFilter: family });
}

/** Reveal or hide dust rows under the fiat threshold in the home list. */
export function setShowDust(value: boolean): void {
  patchSlice('ui', { showDust: value });
}

/**
 * Hide or show one token on one wallet's Tokens tab, persisting the whole map.
 *
 * The token is named by its stable per-wallet identity key (see lib/hiddenTokens),
 * so the same symbol on two EVM networks is toggled independently. Hiding adds the
 * key to the wallet's array; showing removes it, and a wallet whose array becomes
 * empty is dropped from the map so the stored shape stays canonical. A no-op call
 * (hiding an already-hidden key, or showing one not present) leaves the map
 * unchanged and does not notify, so a redundant toggle does not churn subscribers.
 *
 * The full map is written to localStorage before the slice is patched, mirroring
 * the tier selection's persist-then-patch order, so a reload restores the choice.
 * A storage failure is swallowed: the in-memory patch still takes effect for this
 * session and only cross-reload persistence is lost.
 *
 * @param walletId The wallet the token belongs to.
 * @param tokenKey The token's stable identity key.
 * @param hidden True to hide the token, false to show it again.
 */
export function setTokenHidden(walletId: string, tokenKey: string, hidden: boolean): void {
  const current = state.ui.hiddenTokens[walletId] ?? [];
  const has = current.includes(tokenKey);

  // Nothing to do when the target already matches the requested state, so a
  // repeated toggle in the same direction does not replace the slice or write
  // storage needlessly.
  if (hidden === has) return;

  const nextForWallet = hidden
    ? [...current, tokenKey]
    : current.filter((key) => key !== tokenKey);

  const nextMap: Record<string, string[]> = { ...state.ui.hiddenTokens };
  if (nextForWallet.length > 0) {
    nextMap[walletId] = nextForWallet;
  } else {
    // A wallet with nothing hidden carries no entry, keeping the persisted map
    // canonical and matching what persistedHiddenTokens reads back.
    delete nextMap[walletId];
  }

  try {
    localStorage.setItem(HIDDEN_TOKENS_STORAGE_KEY, JSON.stringify(nextMap));
  } catch {
    // Storage can be unavailable (private mode, disabled). The in-memory patch
    // still applies for this session; only cross-reload persistence is lost.
  }
  patchSlice('ui', { hiddenTokens: nextMap });
}

/**
 * The result of an import add: whether the token was added and, when it was not,
 * why. A caller surfaces the reason to the user, for example a full-wallet cap.
 */
export type ImportAddResult =
  | { ok: true }
  | { ok: false; reason: 'cap' };

/**
 * Import a custom token into one wallet's Tokens tab, persisting the whole map.
 *
 * Re-importing a token already present, by its chain-and-contract identity (see
 * lib/importedTokens), refreshes its resolved metadata in place rather than adding
 * a second entry, so it never counts against the cap. A genuinely new import is
 * refused once the wallet already holds the maximum, returning the cap reason so
 * the sheet can explain the limit; the gateway enforces the same bound, so this is
 * a fast local guard, not the trust boundary. The persisted map is written before
 * the slice is patched, mirroring the hidden-token setter's persist-then-patch
 * order, so a reload restores the import. A storage failure is swallowed: the
 * in-memory patch still applies for this session and only cross-reload persistence
 * is lost.
 *
 * Callers add through this interface rather than mutating the map so a later
 * change can sync the same records to the server without touching call sites.
 *
 * @param walletId The wallet the token is imported into.
 * @param token The resolved imported token.
 * @returns Whether the import was added, and the reason when it was not.
 */
export function addImportedToken(walletId: string, token: ImportedToken): ImportAddResult {
  const current = state.ui.importedTokens[walletId] ?? [];
  const isReimport = hasImported(current, token);

  // A genuinely new import past the cap is refused; a re-import is always allowed
  // since it replaces an existing entry and does not grow the list.
  if (!isReimport && current.length >= MAX_IMPORTED_TOKENS_PER_WALLET) {
    return { ok: false, reason: 'cap' };
  }

  // Append then dedupe by identity so a re-import overwrites the earlier metadata
  // at its original position rather than doubling the entry.
  const nextForWallet = dedupeImported([...current, token]);

  const nextMap: Record<string, ImportedToken[]> = { ...state.ui.importedTokens };
  nextMap[walletId] = nextForWallet;

  try {
    localStorage.setItem(IMPORTED_TOKENS_STORAGE_KEY, JSON.stringify(nextMap));
  } catch {
    // Storage can be unavailable (private mode, disabled). The in-memory patch
    // still applies for this session; only cross-reload persistence is lost.
  }
  patchSlice('ui', { importedTokens: nextMap });
  return { ok: true };
}

/**
 * Remove one imported custom token from a wallet, persisting the whole map. The
 * token is named by its stable chain-and-contract identity key, so removing one
 * import never touches another. A wallet whose list becomes empty is dropped from
 * the map so the stored shape stays canonical, and a remove of a key not present
 * is a no-op that leaves the slice reference unchanged so a redundant remove does
 * not churn subscribers.
 *
 * @param walletId The wallet the token was imported into.
 * @param importKey The token's stable identity key (see lib/importedTokens).
 */
export function removeImportedToken(walletId: string, importKey: string): void {
  const current = state.ui.importedTokens[walletId] ?? [];
  const nextForWallet = current.filter((t) => tokenImportKey(t) !== importKey);

  // Nothing matched, so leave the slice untouched rather than replacing it with an
  // equal value and waking subscribers for no change.
  if (nextForWallet.length === current.length) return;

  const nextMap: Record<string, ImportedToken[]> = { ...state.ui.importedTokens };
  if (nextForWallet.length > 0) {
    nextMap[walletId] = nextForWallet;
  } else {
    // A wallet with nothing imported carries no entry, keeping the persisted map
    // canonical and matching what persistedImportedTokens reads back.
    delete nextMap[walletId];
  }

  try {
    localStorage.setItem(IMPORTED_TOKENS_STORAGE_KEY, JSON.stringify(nextMap));
  } catch {
    // Storage can be unavailable; the in-memory patch still applies this session.
  }
  patchSlice('ui', { importedTokens: nextMap });
}

/* In-flight swap tracker mutations. A swap enters the map at execute time and
   leaves it when it settles, so the persisted map is exactly the set the resume
   loop re-arms on boot.

   PERSISTENCE IS A RECORD-LEVEL READ-MODIFY-WRITE against the freshly read
   SHARED store, never a whole-map dump of this tab's in-memory state (codex
   plugin review, blocking). The map is shared across tabs, and a tab only
   learns of another tab's writes via the async `storage` event — so a
   whole-map write from a tab that had not yet received the event silently
   ERASED every record it did not know about, including the double-spend latch
   another tab had just raised. Reading the persisted map at write time and
   mutating only this record makes concurrent tabs conflict only on the SAME
   record id (last-writer-wins there, which is the pre-existing semantic for a
   single record). The in-memory slice remains this tab's view: it applies the
   same record change locally and catches up with everything else on the next
   storage event. */

/**
 * Apply one record-level change to the PERSISTED pending-swaps map: fresh
 * read, mutate, write. Storage failures are swallowed — the in-memory patch
 * still applies for this session and only cross-reload/cross-tab durability is
 * lost, which was this function's pre-existing failure mode.
 */
function persistPendingSwapChange(mutate: (map: Record<string, PendingSwap>) => void): void {
  try {
    const map = persistedPendingSwaps();
    mutate(map);
    localStorage.setItem(PENDING_SWAPS_STORAGE_KEY, JSON.stringify(map));
  } catch {
    // Storage can be unavailable (private mode, disabled).
  }
}

/**
 * Read the persisted pending-swaps map FRESH from storage — the cross-tab
 * truth, not this tab's slice. The submit path uses this as its final latch
 * gate: another tab's ambiguity hold reaches this tab's slice only via the
 * async storage event, and a submit racing that event must still see it.
 */
export function readPersistedPendingSwaps(): Record<string, PendingSwap> {
  return persistedPendingSwaps();
}

/**
 * Insert or replace one in-flight swap, persisting the whole map. Used at
 * execute time to seed the record and on every status tick to fold in the
 * freshest status, sub-status, and destination hash. Keyed by the swap's own id,
 * so a re-upsert of the same swap refreshes it in place rather than duplicating.
 * A storage failure is swallowed: the in-memory patch still drives the tracker
 * for this session and only cross-reload resume is lost.
 *
 * @param swap The pending-swap record to store.
 */
export function upsertPendingSwap(swap: PendingSwap): void {
  persistPendingSwapChange((map) => {
    map[swap.id] = swap;
  });
  patchSlice('pendingSwaps', { byId: { ...state.pendingSwaps.byId, [swap.id]: swap } });
}

/**
 * Merge a patch into an existing pending swap by id, stamping updatedAt, then
 * persist. A no-op when the id is not tracked, so a late status tick for a swap
 * already removed on settlement does not resurrect it. This is the convenience
 * the poller calls each tick rather than rebuilding the whole record.
 *
 * @param id The tracked swap's id.
 * @param patch The fields to merge in.
 */
export function setPendingSwapStatus(id: string, patch: Partial<PendingSwap>): void {
  const current = state.pendingSwaps.byId[id];
  if (!current) return;
  upsertPendingSwap({ ...current, ...patch, id, updatedAt: Date.now() });
}

/**
 * Remove one in-flight swap by id, persisting the whole map. Called when a swap
 * reaches a terminal state so the persisted set stays exactly the not-yet-
 * settled swaps the resume loop must re-arm. A remove of an id not present is a
 * no-op that leaves the slice reference unchanged, so a redundant remove does not
 * wake subscribers.
 *
 * @param id The tracked swap's id.
 */
export function removePendingSwap(id: string): void {
  if (!(id in state.pendingSwaps.byId)) return;
  persistPendingSwapChange((map) => {
    delete map[id];
  });
  const nextMap: Record<string, PendingSwap> = { ...state.pendingSwaps.byId };
  delete nextMap[id];
  patchSlice('pendingSwaps', { byId: nextMap });
}

/**
 * Drop every tracked swap and its persisted storage. Called on sign-out so the
 * next user's session never resumes polling or displaying a swap that belonged
 * to the account that just logged out. A no-op when nothing is tracked leaves the
 * slice reference unchanged so it does not wake subscribers needlessly.
 */
export function clearPendingSwaps(): void {
  try {
    localStorage.removeItem(PENDING_SWAPS_STORAGE_KEY);
  } catch {
    // Storage can be unavailable; the in-memory clear below still applies.
  }
  if (Object.keys(state.pendingSwaps.byId).length === 0 && state.pendingSwaps.inlineId === null) {
    return;
  }
  patchSlice('pendingSwaps', { byId: {}, inlineId: null });
}

/**
 * Record which swap the Swap screen is showing with its own inline stepper, so the
 * floating tracker can skip that one rather than hide itself on the whole route —
 * otherwise a persisted swap (or a second concurrent one) would be shown nowhere.
 *
 * View state, not swap data: the Swap screen sets it while mounted and passes null on
 * unmount. Never persisted, and never gates polling, which runs in the engine.
 */
export function setInlineSwap(id: string | null): void {
  if (state.pendingSwaps.inlineId === id) return; // no-op keeps subscribers asleep
  patchSlice('pendingSwaps', { inlineId: id });
}

/** Monotonic counter for toast ids. */
let toastSeq = 0;

/**
 * How long each kind stays up before it dismisses itself.
 *
 * Toasts were described as a "transient message stack" but nothing ever made them
 * transient: only the close button removed one, so a "Wallet created." sat over
 * the bottom nav until it was tapped, and a run of them stacked up covering the
 * screen.
 *
 * An error gets twice the life of a confirmation because it carries more to read
 * and is worth more if missed — but it still expires. A toast that never leaves
 * has to be dismissed to use the app underneath it, which is a worse failure than
 * one the user has to read quickly; anything truly blocking belongs in the view,
 * not in a toast.
 */
const TOAST_TTL_MS: Record<Toast['kind'], number> = {
  info: 4000,
  success: 4000,
  error: 8000,
};

/**
 * How long the leaving animation runs before the toast is actually removed.
 *
 * Must stay in step with the toast-out animation in 05-components/toast.css: too
 * short and the toast vanishes mid-slide, too long and a gap sits in the stack
 * after it has visually gone.
 */
const TOAST_EXIT_MS = 220;

/**
 * One timer per toast — either the one waiting to start its exit, or the one
 * waiting to finish it, never both. Starting the exit replaces the entry.
 */
const toastTimers = new Map<number, ReturnType<typeof setTimeout>>();

/** Schedule a callback and keep it from holding a Node process (vitest) open. */
function toastTimer(id: number, ms: number, run: () => void): void {
  const timer = setTimeout(run, ms);
  // Browsers have no unref, hence the optional call.
  (timer as { unref?: () => void }).unref?.();
  toastTimers.set(id, timer);
}

/** Drop a toast from the stack, and forget any timer still held for it. */
function removeToast(id: number): void {
  const timer = toastTimers.get(id);
  if (timer !== undefined) {
    clearTimeout(timer);
    toastTimers.delete(id);
  }
  if (!state.ui.toasts.some((t) => t.id === id)) return;
  patchSlice('ui', { toasts: state.ui.toasts.filter((t) => t.id !== id) });
}

/** Push a transient toast message and return its id for later dismissal. */
export function pushToast(kind: Toast['kind'], text: string): number {
  const id = ++toastSeq;
  patchSlice('ui', { toasts: [...state.ui.toasts, { id, kind, text }] });
  toastTimer(id, TOAST_TTL_MS[kind] ?? TOAST_TTL_MS.info, () => dismissToast(id));
  return id;
}

/**
 * Begin dismissing a toast: mark it leaving, then remove it once its animation
 * has run. Called by the close button and by the lifetime timer alike, so a
 * dismissal looks the same however it was triggered.
 *
 * Safe to call twice — a second call on an already-leaving toast is ignored
 * rather than restarting the exit, which would otherwise let a user extend a
 * toast's life indefinitely by hammering the close button.
 */
export function dismissToast(id: number): void {
  // The bail-out comes BEFORE the timer is touched, and that order is the whole
  // point. Clearing first and returning after would cancel the exit already in
  // flight without scheduling another, and the toast would sit on screen for good
  // — a tap on the close button of an already-expiring toast was enough to do it.
  const toast = state.ui.toasts.find((t) => t.id === id);
  if (!toast || toast.exiting) return;

  const timer = toastTimers.get(id);
  if (timer !== undefined) {
    clearTimeout(timer);
    toastTimers.delete(id);
  }
  patchSlice('ui', {
    toasts: state.ui.toasts.map((t) => (t.id === id ? { ...t, exiting: true } : t)),
  });
  toastTimer(id, TOAST_EXIT_MS, () => removeToast(id));
}

/**
 * Reset the whole store to its initial state. TESTS ONLY — every caller today is
 * a `.test.ts`, and it must stay that way.
 *
 * It rebuilds the initial state, which sets sessionEpoch back to 0. That is fine
 * for a fresh test but would break the invariant in a running app: a later session
 * would land on a generation already seen, and the caches keyed on it would skip
 * the reload they exist to force. Logout goes through clearAccount, which carries
 * the epoch across for exactly that reason.
 */
export function resetStore(): void {
  // Toast timers outlive the state they were scheduled against. The ids cannot
  // collide (toastSeq is deliberately not reset here), so a late timer would not
  // dismiss the wrong toast — but it would still wake the store and notify
  // subscribers after the test that created it had finished, which is how a
  // cross-test flake starts.
  for (const timer of toastTimers.values()) clearTimeout(timer);
  toastTimers.clear();
  state = initialState();
  for (const name of Object.keys(state) as SliceName[]) notify(name);
}
